// src/components/kuestion/campus/AddStudent.jsx
//
// The examiner picks the six-character code; the prefix is already theirs.
// Generating the password is the expected path (contract §5) — the generated
// value is shown once with a copy action, because there is no email step.
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import { useCampus } from './CampusContext';
import { Alert, CopyButton, Note } from '../shared/Bits';
import { RefreshIcon, CheckIcon } from '../shared/Icons';
import { generatePassword, passwordProblems, cleanStudentCode, STUDENT_CODE } from '../shared/password';
import { kq } from '../../../config/routes';

export default function AddStudent() {
  const navigate = useNavigate();
  const { org, api, students, reloadStudents } = useCampus();

  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [code, setCode] = useState('');
  const [pw, setPw] = useState('');
  const [generated, setGenerated] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null);

  const taken = useMemo(() => new Set(students.map((s) => String(s.student_code).toUpperCase())), [students]);
  const codeOk = STUDENT_CODE.test(code);
  const codeTaken = codeOk && taken.has(code);
  const codeMsg = !code ? 'Six letters or numbers'
    : !codeOk ? `${6 - code.length} more character${6 - code.length === 1 ? '' : 's'} needed`
      : codeTaken ? 'Already used in your organisation' : 'Available in your organisation';
  const problems = pw ? passwordProblems(pw) : [];
  const login = `${org?.prefix}-${code || '——————'}`;
  const canSubmit = first.trim() && last.trim() && codeOk && !codeTaken && pw && problems.length === 0 && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true); setError('');
    try {
      const data = await api.postJson(API_ENDPOINTS.CAMPUS_STUDENTS, {
        student_code: code,
        first_name: first.trim(),
        last_name: last.trim(),
        password: pw,
      });
      setCreated({ ...data, password: data?.generated_password || pw, login_id: data?.login_id || login });
      reloadStudents();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const reset = () => { setFirst(''); setLast(''); setCode(''); setPw(''); setGenerated(false); setCreated(null); setError(''); };

  if (created) {
    return (
      <div className="kqc-page kqc-page--narrow">
        <span className="kqc-bigcheck kqc-bigcheck--sm"><CheckIcon /></span>
        <h1 className="kqc-h1" style={{ marginBottom: 8 }}>{created.first_name} {created.last_name} can log in</h1>
        <p className="kqc-lede" style={{ marginBottom: 26 }}>Write both down or copy them now. The password is shown once and cannot be retrieved — only reset from the student&rsquo;s page.</p>
        <div className="kqc-box kqc-box--surface" style={{ maxWidth: 480, marginBottom: 26 }}>
          <div className="kqc-box__head">What they will be given</div>
          <div className="kqc-box__body" style={{ padding: '18px 14px' }}>
            <div className="kqc-handout">Login ID</div>
            <div className="is-id">{created.login_id}</div>
            <div className="kqc-handout">Password</div>
            <div className="is-pw">{created.password}</div>
            <div className="kqc-row">
              <CopyButton text={`${created.login_id}\t${created.password}`} label="Copy both" ink />
              <CopyButton text={created.login_id} label="Copy login ID" />
              <CopyButton text={created.password} label="Copy password" />
            </div>
          </div>
        </div>
        <div className="kqc-row">
          <button type="button" className="btn btn-primary" onClick={reset}>Add another student</button>
          <button type="button" className="btn btn-secondary" onClick={() => navigate(kq(`students/${created.id || ''}`))}>Open the roster</button>
        </div>
      </div>
    );
  }

  return (
    <div className="kqc-page kqc-page--narrow">
      <button type="button" className="btn btn-ghost kqc-back" onClick={() => navigate(kq('students'))}>&larr; Students</button>
      <h1 className="kqc-h1" style={{ marginBottom: 8 }}>Add a student</h1>
      <p className="kqc-lede" style={{ marginBottom: 28, maxWidth: '56ch' }}>You choose the six-character code; the prefix is already yours. There is no email step — you hand the login and password over yourself.</p>

      {error && <Alert kind="error" onClose={() => setError('')}>{error}</Alert>}

      <div className="kqc-cols">
        <div className="is-main" style={{ maxWidth: 480 }}>
          <div className="kqc-row kqc-row--gap" style={{ alignItems: 'stretch', marginBottom: 16 }}>
            <div className="field" style={{ flex: '1 1 140px' }}><label htmlFor="fn">First name</label><input id="fn" className="input" type="text" value={first} onChange={(e) => setFirst(e.target.value)} placeholder="Ayesha" autoFocus /></div>
            <div className="field" style={{ flex: '1 1 140px' }}><label htmlFor="ln">Last name</label><input id="ln" className="input" type="text" value={last} onChange={(e) => setLast(e.target.value)} placeholder="Khan" /></div>
          </div>

          <div className="field" style={{ marginBottom: 8 }}>
            <label htmlFor="code">Student code — 6 letters or numbers</label>
            <div className="kqc-affix">
              <span>{org?.prefix}-</span>
              <input id="code" type="text" value={code} onChange={(e) => setCode(cleanStudentCode(e.target.value))} maxLength={6} placeholder="A7K2M9" autoComplete="off" spellCheck={false} />
            </div>
          </div>
          <div className={`kqc-small${codeTaken ? ' kqc-accent-ink' : codeOk ? ' kqc-accent-ink' : ' kqc-muted'}`} style={{ marginBottom: 22 }}>{codeMsg}</div>

          <div className="kqc-box" style={{ marginBottom: 22 }}>
            <div className="kqc-box__head">
              <span>Password</span>
              <span className="kqc-spacer" />
              <button type="button" className="kqc-genbtn" onClick={() => { setPw(generatePassword()); setGenerated(true); }}><RefreshIcon />Generate</button>
            </div>
            {generated && pw ? (
              <div className="kqc-box__body kqc-box__body--surface">
                <div className="kqc-pw">
                  <code>{pw}</code>
                  <CopyButton text={pw} ink />
                </div>
                <Note accent><strong>Copy it now.</strong> This password is shown once and cannot be retrieved. If you lose it, reset it from the student&rsquo;s page.</Note>
                <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 10, minHeight: 0 }} onClick={() => { setPw(''); setGenerated(false); }}>Type one instead</button>
              </div>
            ) : (
              <div className="kqc-box__body">
                <div className="field" style={{ marginBottom: 10 }}>
                  <label htmlFor="pw">Or type one — same policy, no exceptions</label>
                  <input id="pw" className="input kqc-mono" type="text" autoComplete="off" spellCheck={false} value={pw} onChange={(e) => { setPw(e.target.value); setGenerated(false); }} placeholder="12–20 chars, upper, lower, number, symbol" />
                </div>
                {pw && problems.length > 0
                  ? <div className="kqc-small kqc-accent-ink">Needs {problems.join(', ')}.</div>
                  : <div className="kqc-small kqc-muted">Generating is the expected path — it guarantees a compliant password and saves you inventing one.</div>}
              </div>
            )}
          </div>

          <div className="kqc-row">
            <button type="button" className="btn btn-primary" style={{ minHeight: 46 }} disabled={!canSubmit} onClick={submit}>{busy ? 'Creating…' : 'Create student'}</button>
            <button type="button" className="btn btn-secondary" style={{ minHeight: 46 }} onClick={() => navigate(kq('students'))} disabled={busy}>Cancel</button>
          </div>
        </div>

        <div className="is-side" style={{ maxWidth: 360 }}>
          <div className="kqc-box kqc-box--surface">
            <div className="kqc-box__head">What they will be given</div>
            <div className="kqc-box__body" style={{ padding: '18px 14px' }}>
              <div className="kqc-handout">Login ID</div>
              <div className="is-id">{login}</div>
              <div className="kqc-handout">Password</div>
              <div className={`is-pw${pw ? '' : ' is-empty'}`}>{pw || 'Not set yet'}</div>
              <div className="kqc-hr" />
              <div className="kqc-small" style={{ color: 'var(--color-neutral-800)' }}>Write both down, or print the handout. Students have no email and cannot reset their own password.</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
